import React, { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import { tokenStorage } from "../utils/tokenStorage";
import { serviceTokenStore, userTokenStore } from "../utils/serviceTokenStore";
import { endSession as endSessionApi } from "../utils/api";
import { fetchServiceAccountToken, signIn } from "../apis/auth";
import {
  getStoredServiceToken,
  clearStoredServiceToken,
  EXPIRY_BUFFER_MS,
} from "../utils/helpers";
import type { UserData } from "../dtos/patient";
import { useAuth as useOidcAuth } from "react-oidc-context";

interface AuthContextValue {
  serviceAccountToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  error: string | null;
  persistSession: (userData: UserData) => void;
  endSessionAndLogout: (
    sessionId?: string | number,
    podIP?: string
  ) => Promise<void>;
  clearSession: () => void;
  handleAuthentication: () => Promise<void>;
  handleStartSession: (podIp: string) => Promise<void>;
  session: UserData | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<UserData | null>(null);
  const [serviceAccountToken, setServiceAccountToken] = useState<string | null>(() => {
    const existing = getStoredServiceToken();
    if (existing) {
      serviceTokenStore.setToken(existing);
      return existing;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const auth = useOidcAuth();


  /**
   * Fetch the service token and schedule the next refresh before expiry.
   */
  const fetchAndScheduleRefresh = async () => {
    try {
      const { token, expiresIn } = await fetchServiceAccountToken();
      setServiceAccountToken(token);
      serviceTokenStore.setToken(token);

      // Schedule refresh 1 minute before expiry
      if (token && expiresIn > 0) {
        const refreshInMs = Math.max((expiresIn * 1000) - EXPIRY_BUFFER_MS, 0);
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
          clearStoredServiceToken();
          fetchAndScheduleRefresh();
        }, refreshInMs);
      }
    } catch (err) {
      console.error("Service account token fetch failed:", err);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(fetchAndScheduleRefresh, 30_000);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 1. Load service account token and schedule auto-refresh
        await fetchAndScheduleRefresh();

        // 2. Load existing patient session
        const currentSession = tokenStorage.getPodSession();
        setSession(currentSession);
      } catch (err) {
        console.error("Authentication init failed:", err);
        setError("Failed to initialize session");
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [auth.isAuthenticated]);

  const handleAuthentication = async () => {
    if (!auth.isAuthenticated) {
      auth.signinRedirect();
    }
  };

  const handleStartSession = async (podIp: string) => {
    setIsLoading(true);
    if (auth.isAuthenticated) {
      const response = await signIn(podIp, auth.user?.access_token);
      if (response.ok) {
        const data = await response.json();
        persistSession({
          id: data.user_info.user_id,
          patientId: data.pod_checkin_response.patientId,
          sessionId: data.pod_checkin_response.sessionId,
          podIP: podIp,
          name: data.user_info.full_name,
          gender: data.user_info.gender,
          dateOfBirth: data.user_info.date_of_birth,
          age: data.pod_checkin_response.patientAge,
          email: auth.user?.profile?.email ?? data.pod_checkin_response.patientEmail ?? "",
        });
        setIsLoading(false);
        return Promise.resolve();
      }
      setIsLoading(false);
      return Promise.reject(new Error('Failed to start session'));
    }
  }

  /**
   * Initiate local login flow for the patient pod session
   */
  const persistSession = (userData: UserData): void => {
    tokenStorage.setPodSession(userData);
    setSession(userData);
  };

  /**
   * Clear user tokens and pod session locally
   */
  const clearSession = (): void => {
    tokenStorage.clearTokens();
    setSession(null);
    setIsLoggingOut(false);
  };

  const handleLogout = async (podIP: string, sessionId: string | number) => {
    try {
      setIsLoading(true);
      await endSessionApi(podIP, sessionId, auth.user?.access_token);
      clearSession();
      userTokenStore.revokeToken();
      await auth.revokeTokens();
      auth.removeUser();
      auth.signoutRedirect({
        id_token_hint: auth.user?.id_token,
      });
    } catch (err) {
      console.warn("Revoke failed due to header issues, proceeding with local logout", err);
      auth.removeUser();
      auth.signoutRedirect({
        id_token_hint: auth.user?.id_token,
      });
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * End session via API, clear app state, then logout.
   */
  const endSessionAndLogout = async (
    sessionId?: string | number,
    podIP?: string
  ): Promise<void> => {
    setIsLoggingOut(true);
    try {
      if (sessionId && podIP) {
        await handleLogout(podIP, sessionId);
      }
    } catch (error) {
      console.error("Error ending session:", error);
    }

  };

  const value: AuthContextValue = {
    serviceAccountToken,
    isLoading: isLoading || auth.isLoading,
    isAuthenticated: !!session,
    isLoggingOut,
    error,
    session,
    persistSession,
    endSessionAndLogout,
    clearSession,
    handleAuthentication,
    handleStartSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
